import { useEffect, useState } from 'react';
import { socialApi, type AnalyticsSummary } from '@/lib/social-api';
import { formatMetric } from '@/lib/post-metrics';
import { platformMeta } from '@/lib/social-platforms';
import { PlatformIcon } from '@/components/social/PlatformIcon';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function SocialPerformanceCard({ onNavigate }: { onNavigate?: (path: string) => void }) {
  const [data, setData] = useState<AnalyticsSummary | null>(null);

  useEffect(() => {
    void socialApi.analyticsSummary(30).then(setData).catch(() => setData(null));
  }, []);

  const totals = data?.totals ?? { posts: 0, impressions: 0, engagement: 0 };

  return (
    <Card className="w-full border-border bg-card shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">Social-Performance (30 Tage)</CardTitle>
        <button
          type="button"
          className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
          onClick={() => onNavigate?.('/social')}
        >
          Social Hub
        </button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-3">
          <Stat label="Posts" value={String(totals.posts)} />
          <Stat label="Impressionen" value={formatMetric(totals.impressions)} />
          <Stat label="Interaktionen" value={formatMetric(totals.engagement)} />
        </div>

        {(data?.platforms.length ?? 0) === 0 ? (
          <p className="text-sm text-muted">Noch keine Kennzahlen. Veröffentliche Posts und warte auf den Sync.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="pb-2 font-medium">Kanal</th>
                  <th className="pb-2 text-right font-medium">Posts</th>
                  <th className="pb-2 text-right font-medium">Impr.</th>
                  <th className="pb-2 text-right font-medium">Interakt.</th>
                </tr>
              </thead>
              <tbody>
                {data?.platforms.map((row) => (
                  <tr key={row.platform} className="border-t border-border">
                    <td className="py-2">
                      <span className="inline-flex items-center gap-2">
                        <PlatformIcon platform={row.platform} size={16} />
                        {platformMeta(row.platform).label}
                      </span>
                    </td>
                    <td className="py-2 text-right tabular-nums">{row.posts}</td>
                    <td className="py-2 text-right tabular-nums">{formatMetric(row.impressions)}</td>
                    <td className="py-2 text-right tabular-nums">{formatMetric(row.engagement)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(data?.top_posts?.length ?? 0) > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium text-muted">Stärkste Posts</p>
            <ul className="space-y-1.5">
              {data?.top_posts?.map((post) => (
                <li key={post.post_id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 rounded-md px-1 py-1 text-left hover:bg-card-hover"
                    onClick={() => onNavigate?.(`/composer/${post.post_id}`)}
                  >
                    <span className="min-w-0 truncate text-sm text-foreground">{post.title}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted">
                      {formatMetric(post.impressions)} · {formatMetric(post.engagement)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2">
      <p className="text-xs text-muted">{label}</p>
      <p className="text-lg font-semibold tabular-nums text-foreground">{value}</p>
    </div>
  );
}
